/**
 * useCollabRoom — minimal WebSocket hook for realtime canvas collab.
 *
 * Connects to /api/ws/collab/<roomId>, broadcasts via `send()`, and calls
 * `onMessage(msg)` on every inbound payload (peer_id + ts attached by the
 * server). Auto-reconnects with exponential backoff.
 *
 * Usage:
 *   const { send, peerCount, status, clientId } = useCollabRoom(roomId, {
 *     onMessage: (m) => { if (m.kind === 'stroke') applyStroke(m); },
 *   });
 *
 * Pass null/empty roomId to disable. Switching rooms tears the previous
 * socket down cleanly.
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const MAX_RECONNECT_DELAY_MS = 10000;
const BASE_RECONNECT_DELAY_MS = 500;

function wsUrl(roomId) {
  // Same host the REST calls use; an empty REACT_APP_BACKEND_URL means
  // same-origin (API = "/api"), so fall back to the page's own host.
  const base = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '')
    || window.location.origin;
  const wsProto = base.startsWith('https') ? 'wss' : 'ws';
  const host = base.replace(/^https?:\/\//, '');
  // Browsers can't set headers on a WebSocket, so the JWT rides in the query.
  const token = localStorage.getItem('desky_token');
  const auth = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${wsProto}://${host}/api/ws/collab/${encodeURIComponent(roomId)}${auth}`;
}

export default function useCollabRoom(roomId, { onMessage } = {}) {
  const [status, setStatus] = useState('idle'); // idle | connecting | open | closed
  const [peerCount, setPeerCount] = useState(0);
  const [clientId, setClientId] = useState(null);
  const wsRef = useRef(null);
  const onMsgRef = useRef(onMessage);

  useEffect(() => { onMsgRef.current = onMessage; }, [onMessage]);

  useEffect(() => {
    if (!roomId) { setStatus('idle'); return undefined; }
    // `disposed` stops a socket closed by our own cleanup from scheduling a
    // reconnect — otherwise every closed modal leaves a ghost peer behind.
    let disposed = false;
    let attempts = 0;
    let reconnectTimer = null;

    const open = () => {
      if (disposed) return;
      setStatus('connecting');
      let ws;
      try {
        ws = new WebSocket(wsUrl(roomId));
      } catch (_e) {
        setStatus('closed');
        return;
      }
      wsRef.current = ws;
      ws.onopen = () => {
        attempts = 0;
        setStatus('open');
      };
      ws.onmessage = (evt) => {
        let msg;
        try { msg = JSON.parse(evt.data); } catch (_e) { return; }
        if (msg.kind === 'welcome') {
          setClientId(msg.client_id);
          setPeerCount(msg.peer_count || 1);
        } else if (msg.kind === 'presence' && typeof msg.peer_count === 'number') {
          setPeerCount(msg.peer_count);
        }
        if (onMsgRef.current) onMsgRef.current(msg);
      };
      ws.onclose = () => {
        if (disposed) return;
        setStatus('closed');
        const delay = Math.min(MAX_RECONNECT_DELAY_MS, BASE_RECONNECT_DELAY_MS * 2 ** attempts);
        attempts += 1;
        reconnectTimer = setTimeout(open, delay);
      };
      ws.onerror = () => { /* onclose handles reconnect */ };
    };

    open();
    return () => {
      disposed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws) {
        ws.onclose = null;
        ws.onmessage = null;
        ws.close(); // also aborts a socket that is still CONNECTING
      }
      setPeerCount(0);
    };
  }, [roomId]);

  const send = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    try {
      ws.send(JSON.stringify(payload));
      return true;
    } catch (_e) { return false; }
  }, []);

  return { send, status, peerCount, clientId };
}
